/**
 * freshChat — which chat lines get the brief "new message" highlight in the
 * game log, and for how long. Replaces the old map-bar ticker: an incoming
 * message now lights up its own log line, behind the text, then fades.
 *
 * A line is "fresh" when it is either
 *   - another player's message unseen when the game loads (after the
 *     persisted read marker), or
 *   - any message that arrives live while the game is open (yours included).
 * The highlight plays the FIRST time the line actually renders (first glance —
 * a line further down the log lights up when you scroll to it).
 *
 * RN-free for unit testing.
 */
import type { ChatMessageResponse } from '@warcarp/api-types'

/** Entrance: the row's space opens at once, then the message fades slowly into
 *  it over this long… */
export const CHAT_ENTRANCE_MS = 700
/** …then the "new" state holds this long behind the text… */
export const CHAT_HIGHLIGHT_HOLD_MS = 5_000
/** …then fades out slowly. */
export const CHAT_HIGHLIGHT_FADE_MS = 1_200

type FreshMessage = Pick<ChatMessageResponse, 'id' | 'userId'>

/** Others' messages after the read marker. `null` marker = never read: all. */
export function initialFreshIds(
  messages: readonly FreshMessage[],
  seenMessageId: string | null,
  viewerId: string,
): string[] {
  const seenIndex = seenMessageId === null ? -1 : messages.findIndex((message) => message.id === seenMessageId)
  // An unknown marker (trimmed out of the loaded window) means everything
  // loaded is newer than what was read.
  return messages
    .slice(seenIndex + 1)
    .filter((message) => message.userId !== viewerId)
    .map((message) => message.id)
}

/**
 * Live arrivals — including your own just-sent line, so every message that
 * lands in the log while you watch gets its "new" entrance. (Only the backlog
 * on load is limited to others' messages: your own are never unseen.)
 */
export function liveFreshIds(incoming: readonly FreshMessage[], _viewerId: string): string[] {
  return incoming.map((message) => message.id)
}
